import {
  Alert,
  Button,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  styled,
} from "@mui/material";
import React, { useState, useContext, useEffect } from "react";
import PayoutForm from "../payout-management/PayoutForm";
import PlanSetting from "../plan/PlanSetting";
import AdvanceSettings from "../admin/advanced-setting/AdvanceSettings";
import ClubProfile from "../admin/club/ClubProfile";
import ClubCapabilitiesSetup from "./ClubCapabilitiesSetup";
import { AuthContext } from "../auth/AuthContext";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { AccountSetupAPI } from "../../api/AccountSetupAPI";
import { stepperAction } from "../../redux/reducer/stepperReducer";
import { setSelectedClub } from "../../redux/reducer/userReducer";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../component/Loader";
import {
  calendarTypePayload,
  firstIncompleteWizardTab,
  finishSetupPayload,
  HOW_YOU_CHARGE_STEP,
  isValidSetupData,
  setupLoadErrorMessage,
  vendorStepPayload,
  WIZARD_TAB,
} from "../../util/accountSetup";
import {
  calendarTypeFromCapabilities,
  capabilitiesFromAnswers,
  clearClubCapabilitiesCache,
  clubWantsCardPayments,
  defaultClubCapabilityAnswers,
  hasSavedClubCapabilities,
  isPlanRequiredFromCapabilities,
  isSavedCalendarType,
  normalizeClubCapabilityAnswers,
  planRequiredPayload,
} from "../../util/clubCapabilities";
import { PayoutAPI } from "../../api/PayoutAPI";
import { rootAction } from "../../redux/reducer/rootReducer";
import {
  CART_VENDOR_REQUIRED,
  FINANCIAL_VENDOR_PATH,
  isFinancialVendorVerified,
} from "../../util/financialVendorGate";

const rootStyle = getComputedStyle(document.documentElement);
const color = rootStyle.getPropertyValue("--hoverColorBold").trim();

const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  "& .MuiStepConnector-line": {
    borderColor: color,
  },
}));

const applySetupData = (dispatch, data, options = {}) => {
  dispatch(stepperAction.setStepperDetails(data));
  if (data.accountSetupConfigId) {
    dispatch(stepperAction.setConfigId(data.accountSetupConfigId));
  }
  dispatch(stepperAction.setActiveTab(
    options.tab ?? firstIncompleteWizardTab(data.steps, {
      calendarType: options.calendarType,
      hasClubModel: options.hasClubModel,
    })
  ));
};

const apiStepByNumber = (steps = [], stepNumber) =>
  steps.find((step) => Number(step.step) === stepNumber);

const AccountSetup = () => {
  const { getCurrentUser } = useContext(AuthContext);
  const selectedClub = useSelector((store) => store.user.selectedClub);
  const userInfo = useSelector((store) => store.user.userInfo);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const { activeTab, stepperDetails, isPayoutCompleted } = useSelector((store) => store.stepper);
  const [isUpdated, setIsUpdated] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [clubAnswers, setClubAnswers] = useState(defaultClubCapabilityAnswers);
  const [isCapabilityReview, setIsCapabilityReview] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const clubCapabilities = capabilitiesFromAnswers(clubAnswers);

  const navigate = useNavigate();
  const location = useLocation();
  const wantsClubChange = new URLSearchParams(location.search).get("change") === "club";

  const dispatch = useDispatch();
  const fetchStepsDetails = async () => {
    if (!selectedClub?.clubId) {
      return;
    }
    try {
      setIsLoading(true);
      setLoadError("");
      const response = await AccountSetupAPI.getAllSteps(selectedClub.clubId);
      if (!isValidSetupData(response?.Data)) {
        setLoadError("Unable to load account setup.");
        return;
      }
      applySetupData(dispatch, response.Data, {
        clubId: selectedClub.clubId,
        calendarType: selectedClub.calendarType,
        ...(wantsClubChange ? { tab: WIZARD_TAB.CLUB_MODEL } : {}),
      });
    } catch (error) {
      setLoadError(setupLoadErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (selectedClub?.clubId) {
      fetchStepsDetails();
      const answers = selectedClub.clubAnswers
        ? normalizeClubCapabilityAnswers(selectedClub.clubAnswers)
        : defaultClubCapabilityAnswers();
      setClubAnswers(answers);
      setIsCapabilityReview(
        wantsClubChange
          ? Boolean(answers.bookers)
          : isSavedCalendarType(selectedClub.calendarType) && Boolean(answers.bookers)
      );
    }
    setIsUpdated(false);
  }, [isUpdated, selectedClub?.clubId, location.search]);

  const handleSkipVendor = async () => {
    try {
      const response = await AccountSetupAPI.postStepper(vendorStepPayload(selectedClub.clubId));
      if (isValidSetupData(response?.Data)) {
        applySetupData(dispatch, response.Data, {
          tab: WIZARD_TAB.CALENDAR,
          clubId: selectedClub.clubId,
        });
        return;
      }
    } catch (error) {
      // Stay movable even if the step save fails; they can add Stripe later.
    }
    dispatch(stepperAction.setActiveTab(WIZARD_TAB.CALENDAR));
  };

  const handleSaveClubModel = async () => {
    if (!clubCapabilities.length || !selectedClub?.clubId || !isCapabilityReview) {
      return;
    }
    try {
      setSavingModel(true);
      const calendarType = calendarTypeFromCapabilities(clubCapabilities);
      await AccountSetupAPI.putCalendarType(
        calendarTypePayload(selectedClub.clubId, calendarType)
      );
      try {
        await AccountSetupAPI.updateIsPlanRequired(
          planRequiredPayload(selectedClub.clubId, clubCapabilities)
        );
      } catch (error) {
        // Plan step can set membership again; do not block setup.
      }
      clearClubCapabilitiesCache(selectedClub.clubId);
      const nextClub = {
        ...selectedClub,
        clubCapabilities,
        clubAnswers,
        calendarType,
        isPlanRequired: isPlanRequiredFromCapabilities(clubCapabilities),
      };
      dispatch(setSelectedClub(nextClub));
      if (
        clubWantsCardPayments(nextClub)
        && userInfo?.userId
        && wantsClubChange
        && selectedClub?.accountSetupIsCompleted
      ) {
        let vendorReady = false;
        try {
          const verified = await PayoutAPI.postIsUserAdminVerified({
            userId: userInfo.userId,
            clubId: selectedClub.clubId,
          });
          vendorReady = isFinancialVendorVerified(verified);
        } catch (error) {
          vendorReady = false;
        }
        if (!vendorReady) {
          dispatch(rootAction.setWarningMessage({
            id: Date.now(),
            type: "info",
            content: CART_VENDOR_REQUIRED,
          }));
          navigate(FINANCIAL_VENDOR_PATH);
          return;
        }
      }
      if (wantsClubChange && selectedClub?.accountSetupIsCompleted) {
        navigate("/calender-setup");
        return;
      }
      dispatch(stepperAction.setActiveTab(WIZARD_TAB.PLAN));
    } finally {
      setSavingModel(false);
    }
  };

  const handleAfterCompleteAllSteps = async () => {
    try {
      const response = await AccountSetupAPI.postStepper(finishSetupPayload(selectedClub.clubId));
      if (isValidSetupData(response?.Data)) {
        applySetupData(dispatch, response.Data, { clubId: selectedClub.clubId });
      }
      getCurrentUser();
      navigate('/', { state: { message: 'Welcome to Sport Mingle' } });
    } catch (error) {

    }
  }

  const displaySteps = [
    {
      ...HOW_YOU_CHARGE_STEP,
      isStepCompleted: hasSavedClubCapabilities(selectedClub),
    },
    ...(stepperDetails?.steps || []),
  ];
  const planStep = apiStepByNumber(stepperDetails?.steps, 0);
  const vendorStep = apiStepByNumber(stepperDetails?.steps, 1);
  const calendarStep = apiStepByNumber(stepperDetails?.steps, 2);
  const clubStep = apiStepByNumber(stepperDetails?.steps, 3);

  return (
    <div className="account_setup_container">
      {isLoading && <Loader />}
      {loadError && (
        <Alert severity="error" data-testid="account-setup-load-error">
          {loadError}
        </Alert>
      )}
      {!isLoading && !loadError && stepperDetails?.steps?.length > 0 && (
      <>
      <div className="account_setup_stepper">
        <Stepper
          activeStep={activeTab}
          connector={<CustomStepConnector />}
          alternativeLabel
        >
          {displaySteps.map((data, index) => (
            <Step
              key={data.key || data.step}
              onClick={() => {
                dispatch(stepperAction.setActiveTab(index));
                if (index === WIZARD_TAB.CLUB_MODEL) {
                  setIsCapabilityReview(hasSavedClubCapabilities(selectedClub));
                }
              }}
              sx={{ cursor: "pointer" }}
            >
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: (theme) =>
                      activeTab === index
                        ? 'var(--hoverColorBold)'
                        : theme.palette.text.disabled,
                    '&.Mui-active': {
                      color: 'var(--hoverColorBold)',
                    },
                    '&.Mui-completed': {
                      color: 'var(--hoverColorBold)',
                    },
                  },
                }}
              >{data.stepName}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>
      {activeTab === WIZARD_TAB.CLUB_MODEL && isCapabilityReview && (
        <div className="account_setup_btns">
          <Button
            variant="contained"
            onClick={() => setIsCapabilityReview(false)}
            data-testid="change-how-you-charge-answers"
          >
            Change answers
          </Button>
          <Button
            disabled={!clubCapabilities.length || savingModel}
            onClick={handleSaveClubModel}
            variant="contained"
            data-testid="save-how-you-charge"
          >
            Save & Next
          </Button>
        </div>
      )}
      {activeTab === WIZARD_TAB.PLAN && (
        <div className="account_setup_btns">
          <Button
            onClick={() => dispatch(stepperAction.setActiveTab(WIZARD_TAB.CLUB_MODEL))}
            variant="contained"
          >
            Back
          </Button>
          <Button
            disabled={!planStep?.isStepCompleted || (!subscriptionStatus === "active" || !subscriptionStatus === "trialing")}
            onClick={() => dispatch(stepperAction.setActiveTab(WIZARD_TAB.FINANCIAL_VENDOR))}
            variant="contained"
          >
            Save & Next
          </Button>
        </div>
      )}
      {activeTab === WIZARD_TAB.FINANCIAL_VENDOR && (
        <div className="account_setup_btns">
          <Button
            onClick={() => dispatch(stepperAction.setActiveTab(WIZARD_TAB.PLAN))}
            variant="contained"
          >
            Back
          </Button>
          <div className="account_setup_btns-end">
            <Button
              variant="outlined"
              onClick={handleSkipVendor}
              data-testid="skip-vendor-btn"
            >
              Skip for now
            </Button>
            <Button
              disabled={!vendorStep?.isStepCompleted || !isPayoutCompleted}
              onClick={() => dispatch(stepperAction.setActiveTab(WIZARD_TAB.CALENDAR))}
              variant="contained"
            >
              Save & Next
            </Button>
          </div>
        </div>
      )}
      {activeTab === WIZARD_TAB.CALENDAR && (
        <div className="account_setup_btns">
          <Button
            onClick={() => dispatch(stepperAction.setActiveTab(WIZARD_TAB.FINANCIAL_VENDOR))}
            variant="contained"
          >
            Back
          </Button>
          <Button
            disabled={!calendarStep?.isStepCompleted}
            onClick={() => dispatch(stepperAction.setActiveTab(WIZARD_TAB.CLUB))}
            variant="contained"
          >
            Save & Next
          </Button>
        </div>
      )}
      {activeTab === WIZARD_TAB.CLUB && (
        <div className="account_setup_btns">
          <Button
            onClick={() => dispatch(stepperAction.setActiveTab(WIZARD_TAB.CALENDAR))}
            variant="contained"
          >
            Back
          </Button>
          <div className="account_setup_completed">
            <Button disabled={!clubStep?.isStepCompleted} variant="text" onClick={handleAfterCompleteAllSteps}>
              done
            </Button>
          </div>
        </div>
      )}
      <div className="account_setup_body">
        {activeTab === WIZARD_TAB.CLUB_MODEL && (
          <ClubCapabilitiesSetup
            answers={clubAnswers}
            onAnswersChange={setClubAnswers}
            reviewing={isCapabilityReview}
            onReviewingChange={setIsCapabilityReview}
          />
        )}
        {activeTab === WIZARD_TAB.PLAN && (
          <PlanSetting
            setIsUpdated={setIsUpdated}
            setSubscriptionStatus={setSubscriptionStatus}
            subscriptionStatus={subscriptionStatus}
            applySetupData={(data) => applySetupData(dispatch, data, { clubId: selectedClub.clubId })}
          />
        )}
        {activeTab === WIZARD_TAB.FINANCIAL_VENDOR && (
          <PayoutForm
            setIsUpdated={setIsUpdated}
            applySetupData={(data) => applySetupData(dispatch, data, { clubId: selectedClub.clubId })}
          />
        )}

        {activeTab === WIZARD_TAB.CALENDAR && <AdvanceSettings />}
        {activeTab === WIZARD_TAB.CLUB && <ClubProfile />}
      </div>
      </>
      )}
    </div>
  );
};

export default AccountSetup;
